/**
 * CR307: Wiring Debug Panel
 * 
 * Displays real-time debug information about Theme Labs wiring:
 * - Renderer version (v1/v2)
 * - Token source (draft/persisted)
 * - Hash of draft and persisted tokens
 * - CSS variables applied count
 * - Sample CSS vars for verification
 */

import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  Bug,
  ChevronDown,
  ChevronUp,
  Hash,
  Cpu,
  Palette,
  Check,
  Copy,
} from "lucide-react";
import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";
import { resolveGlobalThemeCssVars, THEME_CSS_VARS } from "@/lib/deals/themeTokensCssResolver";
import { toast } from "sonner";

// ================================================================
// TYPES
// ================================================================

interface WiringDebugPanelProps {
  /** Active renderer version */
  renderer: "v1" | "v2";
  /** Whether renderer is forced by rollout config */
  isRendererForced?: boolean;
  /** Token source being used for preview */
  tokenSource: "draft" | "persisted";
  /** Current draft tokens */
  draftTokens: GlobalThemeTokens;
  /** Persisted tokens from DB */
  persistedTokens: GlobalThemeTokens;
  /** Whether there are unsaved changes */
  hasChanges: boolean;
  /** Optional className */
  className?: string;
}

// ================================================================
// HELPERS
// ================================================================

/**
 * Compute a simple hash for token comparison
 */
function computeTokenHash(tokens: GlobalThemeTokens): string {
  const str = JSON.stringify(tokens);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

// Sample CSS vars to display
const SAMPLE_VAR_KEYS = [
  "--wi-card-bg",
  "--wi-header-bg",
  "--wi-body-text",
  "--wi-muted-text",
  "--wi-card-radius",
  "--wi-card-padding",
] as const;

// ================================================================
// COMPONENT
// ================================================================

export function WiringDebugPanel({
  renderer,
  isRendererForced = false,
  tokenSource,
  draftTokens,
  persistedTokens,
  hasChanges,
  className,
}: WiringDebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Compute hashes
  const draftHash = useMemo(() => computeTokenHash(draftTokens), [draftTokens]);
  const persistedHash = useMemo(() => computeTokenHash(persistedTokens), [persistedTokens]);
  
  // Resolve CSS vars for the active token source
  const activeTokens = tokenSource === "draft" ? draftTokens : persistedTokens;
  const cssVars = useMemo(() => resolveGlobalThemeCssVars(activeTokens), [activeTokens]);
  
  // Count CSS vars
  const varCount = Object.keys(cssVars).length;
  
  // Sample vars for display
  const sampleVars = useMemo(() => {
    return SAMPLE_VAR_KEYS.map(key => ({
      key,
      value: cssVars[key] || "—",
    }));
  }, [cssVars]);
  
  // Copy debug info
  const handleCopyDebug = async () => {
    const debugPayload = {
      timestamp: new Date().toISOString(),
      renderer,
      isRendererForced,
      tokenSource,
      draftHash,
      persistedHash,
      hasChanges,
      varCount,
      sampleVars: sampleVars.reduce((acc, v) => ({ ...acc, [v.key]: v.value }), {}),
      allCssVars: cssVars,
    };
    
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugPayload, null, 2));
      setCopied(true);
      toast.success("Wiring debug JSON copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Failed to copy");
    }
  };
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
        >
          <Bug className="h-3 w-3" />
          Wiring Debug
          <Badge 
            variant={renderer === "v2" ? "default" : "secondary"} 
            className="h-4 px-1 text-[9px]"
          >
            {renderer.toUpperCase()}
          </Badge>
          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-2">
        <div className="p-3 bg-muted/50 rounded-md border text-xs space-y-3">
          {/* Renderer Info */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="text-muted-foreground flex items-center gap-1">
                <Cpu className="h-3 w-3" />
                Renderer:
              </span>
              <Badge 
                variant={renderer === "v2" ? "default" : "secondary"} 
                className="text-[10px]"
              >
                {renderer.toUpperCase()}
                {isRendererForced && " (Forced)"}
              </Badge>
            </div>
            
            <div className="space-y-1">
              <span className="text-muted-foreground flex items-center gap-1">
                <Palette className="h-3 w-3" />
                Token Source:
              </span>
              <Badge 
                variant={tokenSource === "draft" ? "default" : "outline"} 
                className="text-[10px]"
              >
                {tokenSource === "draft" ? "DRAFT" : "PERSISTED"}
              </Badge>
            </div>
          </div>
          
          {/* Hashes */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="text-muted-foreground flex items-center gap-1">
                <Hash className="h-3 w-3" />
                Draft Hash:
              </span>
              <code className="bg-background px-1.5 py-0.5 rounded text-[10px] font-mono">
                {draftHash}
              </code>
            </div>
            
            <div className="space-y-1">
              <span className="text-muted-foreground flex items-center gap-1">
                <Hash className="h-3 w-3" />
                Persisted Hash:
              </span>
              <code className="bg-background px-1.5 py-0.5 rounded text-[10px] font-mono">
                {persistedHash}
              </code>
            </div>
          </div>
          
          {/* Hash match indicator */}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Hashes match:</span>
            <Badge 
              variant={draftHash === persistedHash ? "outline" : "secondary"} 
              className="text-[10px]"
            >
              {draftHash === persistedHash ? "✓ Yes (no changes)" : "✗ No (unsaved changes)"}
            </Badge>
          </div>
          
          <Separator />
          
          {/* CSS Vars */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                CSS vars applied: <strong>{varCount}</strong>
              </span>
              <Badge variant="outline" className="text-[9px]">
                --wi-* prefix
              </Badge>
            </div>
            
            {/* Sample vars */}
            <div className="bg-background rounded p-2 space-y-1">
              <span className="text-[10px] text-muted-foreground">Sample values:</span>
              {sampleVars.map(({ key, value }) => (
                <div key={key} className="flex justify-between text-[10px] font-mono">
                  <span className="text-muted-foreground">{key}:</span>
                  <span className="text-foreground truncate max-w-[120px]" title={value}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          <Separator />
          
          {/* Actions */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] gap-1"
              onClick={handleCopyDebug}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy Debug JSON"}
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default WiringDebugPanel;
